import {
  SD_FONTS,
  SD_FONT_SIZE_REDUCTION,
  SD_MIN_FONT_SIZE,
} from '../config/sportsDistrictBrand';

const DEFAULT_BODY_FONT = '"Inter", sans-serif';
const DEFAULT_DISPLAY_FONT = 'Roboto, sans-serif';

export type SdFontRole = 'display' | 'body';

/** Monument Extended headings — reduce px size for Sports District. */
export const getSdDisplayFontSizePx = (defaultPx: number, isSportsDistrict: boolean): string => {
  if (!isSportsDistrict) {
    return `${defaultPx}px`;
  }

  const reduced = Math.max(SD_MIN_FONT_SIZE, defaultPx - SD_FONT_SIZE_REDUCTION);
  return `${reduced}px`;
};

/** Inter / UI text — keep original px size for readability. */
export const getSdBodyFontSizePx = (defaultPx: number): string => `${defaultPx}px`;

/** @deprecated alias — use getSdDisplayFontSizePx */
export const getSdFontSizePx = getSdDisplayFontSizePx;

type ThemeWithSportsDistrict = {
  typography?: { fontFamily?: string };
  sportsDistrict?: {
    isSportsDistrict?: boolean;
    fontSize?: (defaultPx: number) => string;
    bodyFontSize?: (defaultPx: number) => string;
  };
};

export const isThemeSportsDistrict = (theme: ThemeWithSportsDistrict): boolean =>
  theme.sportsDistrict?.isSportsDistrict ?? false;

/** Display text (Monument Extended) — scaled down for Sports District. */
export const getThemeFontSize = (theme: ThemeWithSportsDistrict, defaultPx: number): string =>
  theme.sportsDistrict?.fontSize?.(defaultPx) ?? `${defaultPx}px`;

/** Body / UI text (Inter) — no px reduction. */
export const getThemeBodyFontSize = (theme: ThemeWithSportsDistrict, defaultPx: number): string =>
  theme.sportsDistrict?.bodyFontSize?.(defaultPx) ?? `${defaultPx}px`;

/**
 * Monument Extended at nominal px (no reduction).
 * Use for calendar chips, dates, slot/duration labels.
 */
export const getThemeUiFontSize = getThemeBodyFontSize;

export const getThemeFontFamily = (
  theme: ThemeWithSportsDistrict,
  role: SdFontRole = 'display',
  fallback?: string,
): string => {
  if (!isThemeSportsDistrict(theme)) {
    if (role === 'body') {
      return fallback ?? DEFAULT_BODY_FONT;
    }
    return fallback ?? theme.typography?.fontFamily ?? DEFAULT_DISPLAY_FONT;
  }

  return role === 'body' ? SD_FONTS.body : SD_FONTS.display;
};

export const createSportsDistrictThemeExtension = (isSportsDistrict: boolean) => ({
  isSportsDistrict,
  fontSize: (defaultPx: number) => getSdDisplayFontSizePx(defaultPx, isSportsDistrict),
  bodyFontSize: (defaultPx: number) => getSdBodyFontSizePx(defaultPx),
});
